import { useAuthInfo } from "@propelauth/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAIResponseStream from "../../../../../components/agno/hooks/useAIResponseStream";
import APIRoutes from "../../../../../components/agno/vars";
import { constructEndpointUrl } from "../../../../../lib/constructEndpointUrl";
import { mergeStreamingContent } from "../../../../../lib/mergeStreamingContent";
import useStore from "../../../../../components/agno/store";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { RunEvent } from "../../../../../types/os";
import { parseHealthDraftFromContent } from "../healthScoreBuilderHelpers";

export interface HealthBuilderMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  isLoading?: boolean;
  isError?: boolean;
}

interface UseHealthModelBuilderStreamOptions {
  scopeKey?: string | null;
  builderContext?: Record<string, unknown> | null;
}

interface SendHealthModelBuilderMessageOptions {
  omitUserMessage?: boolean;
}

function createHealthModelBuilderSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `health-builder-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function useHealthModelBuilderStream(
  options?: UseHealthModelBuilderStreamOptions,
) {
  const scopeKey = options?.scopeKey || "general:default";
  const builderContext = options?.builderContext || null;
  const [messages, setMessages] = useState<HealthBuilderMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const pendingMessageRef = useRef<{
    text: string;
    options?: SendHealthModelBuilderMessageOptions;
  } | null>(null);

  const { streamResponse } = useAIResponseStream();
  const authInfo: any = useAuthInfo();
  const workspaceId =
    useWorkspaceStore((state) => state.workspace?.id)
    || (typeof window !== "undefined" ? localStorage.getItem("he_defws") : null);
  const selectedEndpoint = useStore((state) => state.selectedEndpoint);
  const modelConfig = useStore((state) => state.modelConfig);
  const sessionKey = workspaceId
    ? `he_health_model_builder_session:${workspaceId}:${scopeKey}`
    : `he_health_model_builder_session:${scopeKey}`;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(sessionKey);
    const nextSessionId = saved || createHealthModelBuilderSessionId();
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    if (!saved) {
      localStorage.setItem(sessionKey, nextSessionId);
    }
    setMessages([]);
    pendingMessageRef.current = null;
  }, [sessionKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionIdRef.current = sessionId;
    if (sessionId) {
      localStorage.setItem(sessionKey, sessionId);
    }
  }, [sessionId, sessionKey]);

  useEffect(() => {
    if (!isStreaming && pendingMessageRef.current) {
      const queued = pendingMessageRef.current;
      pendingMessageRef.current = null;
      window.setTimeout(() => {
        void sendMessage(queued.text, queued.options);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const buildRequestHeaders = useCallback((activeSessionId: string | null) => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (authInfo?.accessToken) headers.Authorization = `Bearer ${authInfo.accessToken}`;
    if (authInfo?.user?.userId) headers["X-User-Id"] = authInfo.user.userId;
    if (workspaceId) headers["X-Workspace-Id"] = String(workspaceId);
    if (activeSessionId) headers["X-Session-Id"] = activeSessionId;
    return headers;
  }, [authInfo?.accessToken, authInfo?.user?.userId, workspaceId]);

  const sendMessage = useCallback(async (
    text: string,
    sendOptions?: SendHealthModelBuilderMessageOptions,
  ) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    if (isStreaming) {
      pendingMessageRef.current = { text: trimmed, options: sendOptions };
      return;
    }

    const activeSessionId = sessionIdRef.current || sessionId;
    const endpointUrl = constructEndpointUrl(selectedEndpoint);
    const runUrl = APIRoutes.SkillsStream(endpointUrl);
    const headers = buildRequestHeaders(activeSessionId);
    const assistantMsg: HealthBuilderMessage = {
      id: `assistant-${Date.now()}-${Math.random()}`,
      role: "assistant",
      content: "",
      isLoading: true,
    };

    setMessages((current) => {
      const next = [...current];
      if (!sendOptions?.omitUserMessage) {
        next.push({
          id: `user-${Date.now()}-${Math.random()}`,
          role: "user",
          content: trimmed,
        });
      }
      next.push(assistantMsg);
      return next;
    });
    setIsStreaming(true);
    setStreamError(null);

    const requestBody: Record<string, unknown> = {
      message: trimmed,
      agent_id: "health_model_builder",
      model_config: { model_key: modelConfig.model_key },
      state: {
        health_model_builder_mode: true,
        ...(builderContext ? { health_model_builder_context: builderContext } : {}),
      },
    };

    try {
      await streamResponse({
        apiUrl: runUrl,
        headers,
        requestBody,
        onChunk: (chunk: any) => {
          if (chunk.session_id) {
            const incomingSessionId = String(chunk.session_id);
            sessionIdRef.current = incomingSessionId;
            setSessionId((current) => (current === incomingSessionId ? current : incomingSessionId));
          }

          if (chunk.event === RunEvent.FirstToken) {
            setIsAssistantTyping(true);
            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];
              if (last?.role === "assistant") {
                last.isLoading = false;
                last.content = "";
              }
              return next;
            });
            return;
          }

          if (
            chunk.event === RunEvent.RunContent
            || chunk.event === RunEvent.TeamRunContent
          ) {
            if (typeof chunk.content !== "string") return;

            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];
              if (last?.role === "assistant") {
                last.content = mergeStreamingContent(last.content || "", chunk.content as string);
                last.isLoading = false;
              }
              return next;
            });
            return;
          }

          if (
            chunk.event === RunEvent.RunCompletedEvent
            || chunk.event === RunEvent.RunCompleted
            || chunk.event === RunEvent.TeamRunCompleted
          ) {
            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];

              if (last?.role === "assistant") {
                if (typeof chunk.content === "string" && chunk.content.trim().length > 0) {
                  const existing = last.content.trim();
                  const incoming = chunk.content.trim();
                  last.content = existing && incoming.startsWith(existing) ? existing : incoming;
                }
                last.isLoading = false;
              }

              return next;
            });
          }
        },
        onError: (error) => {
          const message = error.message || "Sorry, something went wrong. Please try again.";
          setStreamError(message);
          setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (last?.role === "assistant") {
              last.content = message;
              last.isLoading = false;
              last.isError = true;
              return next;
            }

            return [
              ...next,
              {
                id: `assistant-error-${Date.now()}`,
                role: "assistant",
                content: message,
                isError: true,
              },
            ];
          });
        },
        onComplete: () => {
          setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (last?.role === "assistant") {
              last.isLoading = false;
            }
            return next;
          });
        },
      });
    } finally {
      setIsStreaming(false);
      setIsAssistantTyping(false);
    }
  }, [
    buildRequestHeaders,
    builderContext,
    isStreaming,
    modelConfig.model_key,
    selectedEndpoint,
    sessionId,
    streamResponse,
  ]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setStreamError(null);
    const nextSessionId = createHealthModelBuilderSessionId();
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    pendingMessageRef.current = null;
    if (typeof window !== 'undefined') {
      localStorage.setItem(sessionKey, nextSessionId);
    }
  }, [sessionKey]);

  const parsedMessages = useMemo(
    () =>
      messages.map((msg) => {
        if (msg.role !== "assistant" || !msg.content) return msg;
        const parsed = parseHealthDraftFromContent(msg.content);
        if (parsed.draftContent === null && !parsed.isDraftStreaming) return msg;
        return { ...msg, content: parsed.chatContent };
      }),
    [messages],
  );

  const latestDraft = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== "assistant" || !msg.content) continue;
      const parsed = parseHealthDraftFromContent(msg.content);
      if (parsed.draftContent !== null) {
        return { content: parsed.draftContent, isStreaming: parsed.isDraftStreaming };
      }
    }
    return null;
  }, [messages]);

  return {
    isAssistantTyping,
    isStreaming,
    streamError,
    messages: parsedMessages,
    latestDraft,
    resetChat,
    sendMessage,
  };
}
